import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { apiFetch } from '../lib/api';

const Schedule = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, actionsRes] = await Promise.all([
        apiFetch('/api/projects'),
        apiFetch('/api/actions')
      ]);
      
      const projectsData = await projectsRes.json();
      const actionsData = await actionsRes.json();
      
      setProjects(projectsData);
      setActions(actionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all reminder dates from projects and actions
  const getReminderDates = () => {
    const dates = [];
    
    // Add project delivery dates
    projects.forEach(project => {
      if (project.estimated_delivery) {
        dates.push({
          date: project.estimated_delivery,
          type: 'delivery',
          title: `Livraison: ${project.client_name}`,
          project: project.client_name,
          projectId: project.id,
          status: project.status
        });
      }
      
      // Add phase end dates
      project.phases?.forEach(phase => {
        if (phase.end_date && phase.status !== 'Terminé') {
          dates.push({
            date: phase.end_date,
            type: 'phase',
            title: `${phase.name}: ${project.client_name}`,
            project: project.client_name,
            projectId: project.id,
            phase: phase.name,
            status: phase.status
          });
        }
      });
    });
    
    // Add action due dates
    actions.forEach(action => {
      if (action.due_date) {
        dates.push({
          date: action.due_date,
          type: 'action',
          title: action.title,
          project: action.project_name,
          projectId: action.project_id,
          status: action.status,
          priority: action.priority
        });
      }
    });
    
    return dates.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const reminderDates = getReminderDates();

  // Get reminders for a specific date
  const getRemindersForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return reminderDates.filter(r => r.date === dateStr);
  };

  // Get upcoming reminders (next 7 days)
  const getUpcomingReminders = () => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    return reminderDates.filter(r => {
      const reminderDate = new Date(r.date);
      return reminderDate >= today && reminderDate <= nextWeek;
    });
  };

  // Calendar generation
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const hasReminders = (date) => {
    if (!date) return false;
    return getRemindersForDate(date).length > 0;
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Excellent': return 'text-green-600 bg-green-50 border-green-200';
      case 'Bon': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'À risque': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Critique': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPhaseColor = (status) => {
    switch (status) {
      case 'Terminé': return 'bg-green-500';
      case 'En cours': return 'bg-blue-500';
      case 'En attente client': return 'bg-orange-500';
      case 'Non démarré': return 'bg-gray-300';
      default: return 'bg-gray-300';
    }
  };

  const atRiskProjects = projects.filter(p => p.health_score === 'À risque' || p.health_score === 'Critique');
  const upcomingReminders = getUpcomingReminders();
  const days = getDaysInMonth(currentDate);
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  return (
    <div className="space-y-6" data-testid="schedule-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="page-title">
            Planning & Agenda
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Calendrier et dates de rappel de vos projets
          </p>
        </div>
      </div>

      {/* Risk Alerts */}
      {atRiskProjects.length > 0 && (
        <Card className="p-4 border-orange-200 bg-orange-50" data-testid="risk-alerts">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-orange-900 mb-1">
                Risques planning détectés
              </h3>
              <p className="text-sm text-orange-700">
                {atRiskProjects.length} projet(s) nécessite(nt) votre attention
              </p>
              <div className="mt-2 space-y-1">
                {atRiskProjects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => navigate(`/project/${project.id}`)}
                    className="text-sm text-orange-800 hover:underline block"
                  >
                    • {project.client_name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="p-6 lg:col-span-2" data-testid="calendar">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={previousMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Day names */}
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {days.map((day, index) => {
              const reminders = day ? getRemindersForDate(day) : [];
              const isSelected = selectedDate && day && selectedDate.toDateString() === day.toDateString();
              
              return (
                <button
                  key={index}
                  onClick={() => day && setSelectedDate(day)}
                  disabled={!day}
                  className={`
                    aspect-square p-2 rounded-lg text-sm transition-colors relative
                    ${!day ? 'invisible' : ''}
                    ${isToday(day) ? 'bg-primary text-primary-foreground font-bold' : ''}
                    ${isSelected ? 'ring-2 ring-primary' : ''}
                    ${!isToday(day) && !isSelected ? 'hover:bg-accent' : ''}
                    ${hasReminders(day) && !isToday(day) ? 'bg-blue-50' : ''}
                  `}
                  data-testid={`calendar-day-${day?.getDate()}`}
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <span>{day?.getDate()}</span>
                    {reminders.length > 0 && (
                      <div className="flex gap-0.5 mt-1">
                        {reminders.slice(0, 3).map((_, i) => (
                          <div key={i} className="w-1 h-1 rounded-full bg-primary" />
                        ))}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected Date Reminders */}
          {selectedDate && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-semibold mb-3">
                Rappels pour le {selectedDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </h3>
              {getRemindersForDate(selectedDate).length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun rappel pour cette date</p>
              ) : (
                <div className="space-y-2">
                  {getRemindersForDate(selectedDate).map((reminder, idx) => (
                    <div
                      key={idx}
                      onClick={() => navigate(`/project/${reminder.projectId}`)}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                    >
                      {reminder.type === 'delivery' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                      {reminder.type === 'phase' && <Clock className="w-4 h-4 text-blue-600" />}
                      {reminder.type === 'action' && <AlertTriangle className="w-4 h-4 text-orange-600" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{reminder.title}</p>
                        <p className="text-xs text-muted-foreground">{reminder.project}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {reminder.type === 'delivery' ? 'Livraison' : reminder.type === 'phase' ? 'Phase' : 'Action'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Upcoming Reminders */}
        <Card className="p-6" data-testid="upcoming-reminders">
          <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Prochains rappels (7 jours)
          </h2>
          
          {upcomingReminders.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun rappel à venir</p>
          ) : (
            <div className="space-y-3">
              {upcomingReminders.map((reminder, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => navigate(`/project/${reminder.projectId}`)}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                  data-testid={`reminder-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-muted-foreground">
                        {new Date(reminder.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {reminder.type === 'delivery' ? 'Livraison' : reminder.type === 'phase' ? 'Phase' : 'Action'}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium truncate">{reminder.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{reminder.project}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Projects Timeline */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement...</div>
      ) : (
        <div className="space-y-4" data-testid="projects-timeline">
          <h2 className="text-base font-semibold">Timeline des projets</h2>
          {projects.map((project, idx) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/project/${project.id}`)} data-testid={`project-timeline-${idx}`}>
                {/* Project Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{project.client_name}</h3>
                      <Badge variant="outline">{project.project_type}</Badge>
                      <Badge className={getHealthColor(project.health_score)}>
                        {project.health_score}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Chef de projet: {project.project_manager}</span>
                      <span>•</span>
                      <span>Début: {project.start_date}</span>
                      <span>•</span>
                      <span>Livraison: {project.estimated_delivery}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary">{project.progress}%</div>
                    <div className="text-xs text-muted-foreground">Progression</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <Progress value={project.progress} className="h-2 mb-4" />

                {/* Phases Timeline */}
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground mb-3">Phases du projet</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {project.phases?.map((phase, phaseIdx) => (
                      <div
                        key={phaseIdx}
                        className="flex items-center gap-2 p-2 rounded-lg border bg-card"
                        data-testid={`phase-${idx}-${phaseIdx}`}
                      >
                        <div className={`w-2 h-2 rounded-full ${getPhaseColor(phase.status)}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{phase.name}</div>
                          <div className="text-xs text-muted-foreground">{phase.status}</div>
                        </div>
                        {phase.progress > 0 && (
                          <div className="text-xs font-medium text-muted-foreground">
                            {phase.progress}%
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}

          {projects.length === 0 && (
            <Card className="p-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucun projet</h3>
              <p className="text-muted-foreground">Commencez par créer votre premier projet</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default Schedule;
